import { useState, useEffect, useCallback } from "react";
import { useDebounce } from "@/hooks/use-debounce";

// Tailwind CSS breakpoints
const breakpoints = {
  xs: 390,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
} as const;

type Breakpoint = keyof typeof breakpoints;

interface UseResponsiveReturn {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isLarge: boolean;
  width: number;
  breakpoint: Breakpoint;
}

export function useResponsive(): UseResponsiveReturn {
  const [windowWidth, setWindowWidth] = useState<number>(() => {
    // Handle SSR by checking if window exists
    if (typeof window !== 'undefined') {
      return window.innerWidth;
    }
    return 1024; // Default fallback for SSR
  });

  // Debounce width changes to prevent excessive re-renders
  const debouncedWidth = useDebounce(windowWidth, 100);

  const handleResize = useCallback(() => {
    setWindowWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    // Set initial width if not set during SSR
    if (typeof window !== 'undefined') {
      setWindowWidth(window.innerWidth);
    }

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  // Determine current breakpoint
  const getCurrentBreakpoint = (width: number): Breakpoint => {
    if (width >= breakpoints['2xl']) return '2xl';
    if (width >= breakpoints.xl) return 'xl';
    if (width >= breakpoints.lg) return 'lg';
    if (width >= breakpoints.md) return 'md';
    if (width >= breakpoints.sm) return 'sm';
    return 'xs';
  };

  const currentBreakpoint = getCurrentBreakpoint(debouncedWidth);

  return {
    isMobile: debouncedWidth < breakpoints.md, // < 768px (xs, sm)
    isTablet: debouncedWidth >= breakpoints.md && debouncedWidth < breakpoints.lg, // 768px - 1023px
    isDesktop: debouncedWidth >= breakpoints.lg, // >= 1024px
    isLarge: debouncedWidth >= breakpoints.xl, // >= 1280px
    width: debouncedWidth,
    breakpoint: currentBreakpoint,
  };
}

// Backward compatibility - keep the old hook but make it better
export function useIsMobile(breakpoint: number = breakpoints.lg): boolean {
  const { width } = useResponsive();
  return width < breakpoint;
}

// Additional utility hooks for specific use cases
export function useIsTablet(): boolean {
  const { isTablet } = useResponsive();
  return isTablet;
}

export function useIsDesktop(): boolean {
  const { isDesktop } = useResponsive();
  return isDesktop;
}

// Hook to check if current width is at or above a specific breakpoint
export function useBreakpoint(bp: Breakpoint): boolean {
  const { width } = useResponsive();
  return width >= breakpoints[bp];
}

// Hook to check if current width is extra small (<= 390px)
export function useIsExtraSmall(): boolean {
  const { width } = useResponsive();
  return width <= breakpoints.xs;
}